import { useMemo, useState } from 'react';
import ActionLink from '../../../../Components/Dashboard/ActionLink';
import API from '../../../../Utils/api';
import useAsync from '../../../../Hooks/useAsync';
import Card from '../../../../Components/Dashboard/Card';
import { CardEmpty, CardError, CardLoading } from '../../../../Components/Dashboard/CardState';
import AthleteActivityRow from './AthleteActivityRow';
import AthleteActivityToolbar from './AthleteActivityToolbar';
import { buildActivityView, toAthleteActivityRows } from './athleteActivityModel';
import useCoachRoutes from '../useCoachRoutes';
import styles from './AthleteActivityCard.module.css';

const TITLE = 'Athletes & activity';

const AthleteActivityCard = ({ coachId, now: nowProp }) => {
    const routes = useCoachRoutes();
    const { status, data, error, reload } = useAsync(
        () => API.getAthletesWithLastWorkoutById(coachId).then(res => toAthleteActivityRows(res.data)),
        [coachId]
    );
    const [filter, setFilter] = useState('all');
    const [sort, setSort] = useState('recent');
    const [query, setQuery] = useState('');
    const now = useMemo(() => nowProp || new Date(), [nowProp, data]); // eslint-disable-line react-hooks/exhaustive-deps

    const view = useMemo(
        () => (data ? buildActivityView(data, { status: filter, sort, query }, now) : null),
        [data, filter, sort, query, now]
    );

    let body;
    let foot;
    if (status === 'loading' && !data) {
        body = <CardLoading label={TITLE} />;
    } else if (status === 'error') {
        body = <CardError label={TITLE} status={error?.response?.status} onRetry={reload} />;
    } else if (data && data.length === 0) {
        body = <CardEmpty>No active athletes yet. Add athletes from the Athletes page.</CardEmpty>;
    } else if (view) {
        body = (
            <>
                <AthleteActivityToolbar
                    counts={view.counts}
                    status={filter}
                    onStatus={setFilter}
                    sort={sort}
                    onSort={setSort}
                    query={query}
                    onQuery={setQuery}
                />
                <div className={styles.cols} aria-hidden="true">
                    <span>Athlete</span>
                    <span>Last completed</span>
                    <span style={{ textAlign: 'right' }}>CSS pace /100 m</span>
                </div>
                {view.visible.length ? (
                    <ul className={styles.list}>
                        {view.visible.map(row => (
                            <AthleteActivityRow key={row.id} row={row} now={now} />
                        ))}
                    </ul>
                ) : (
                    <CardEmpty>No athletes match. Clear the search or choose 'All'.</CardEmpty>
                )}
            </>
        );
        foot = (
            <>
                <span aria-live="polite">
                    Showing {view.visible.length} of {view.total} athletes
                </span>
                <ActionLink to={routes.athletes}>View all</ActionLink>
            </>
        );
    }

    return (
        <Card
            id="roster"
            module="coach-roster"
            title={TITLE}
            count={data ? data.length : undefined}
            busy={status === 'loading'}
            flush
            foot={foot}
            className={styles.card}
        >
            {body}
        </Card>
    );
};

export default AthleteActivityCard;
